import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Nav, Card, Form, Button, Spinner, Badge, Table, Alert } from 'react-bootstrap';
import { FaUser, FaLock, FaPalette, FaCheck, FaShieldAlt, FaEnvelope, FaMobile, FaTimes, FaBuilding, FaBriefcase } from 'react-icons/fa';
import { BsSpeedometer, BsGraphUp, BsPieChartFill } from 'react-icons/bs';
import { toast } from 'react-toastify';
import userService from '../services/userService';
import authService from '../services/authService';
import userPreferenceService from '../services/userPreferenceService';
import appointmentService from '../services/AppointmentService';
import XPortionService from '../services/XPortionService';
import ProfileImage from './ProfileImage';
import { AuthContext } from '../context/AuthContext';
import { API_BASE_URL } from '../configs/apiConfig';
import '../assets/styles/Profile.css';
import SettingsService from '../services/SettingsService';
import FileService from '../services/FileService';

const Profile = ({ isModal = false, onClose }) => {
    const { user: authUser } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('info');
    const [userData, setUserData] = useState(null);
    const [preferences, setPreferences] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tempTwoFactorMethod, setTempTwoFactorMethod] = useState(null);
    const [expandedRowIndex, setExpandedRowIndex] = useState(null);
    const [totalOrgPortionValue, setTotalOrgPortionValue] = useState(0);
    const [tempPreferences, setTempPreferences] = useState({
        darkMode: false,
        twoFactorMethod: '',
        formOpacity: 1.0,
        formBlur: 1.0,
        backgroundBlur: 0.0,
        backgroundImage: null,
        baseColor: "#3498db"
    });

    // Form states
    const [userInfo, setUserInfo] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        mobileNumber: '',
        address: '',
        postCode: '',
        birthDate: null
    });

    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (authUser?.userId) {
            fetchUserData();
            fetchUserAppointments();
        }
    }, [authUser]);

    useEffect(() => {
        if (preferences) {
            setTempPreferences({
                darkMode: preferences.darkMode ?? false,
                twoFactorMethod: preferences.twoFactorMethod ?? '',
                formOpacity: preferences.formOpacity ?? 1.0,
                formBlur: preferences.formBlur ?? 1.0,
                backgroundBlur: preferences.backgroundBlur ?? 0.0,
                backgroundImage: preferences.backgroundImage,
                baseColor: preferences.baseColor || "#3498db"
            });
        }
    }, [preferences]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const userInfo = await userService.getUserById(authUser.userId);
            if (userInfo) {
                setUserData(userInfo);
                setUserInfo({
                    firstName: userInfo.firstName || '',
                    lastName: userInfo.lastName || '',
                    email: userInfo.email || '',
                    phoneNumber: userInfo.phoneNumber || '',
                    mobileNumber: userInfo.mobileNumber || '',
                    address: userInfo.address || '',
                    postCode: userInfo.postCode || '',
                    birthDate: userInfo.birthDate ? new Date(userInfo.birthDate) : null
                });

                try {
                    // Try to get user preferences first
                    const prefs = await userPreferenceService.getUserPreference(authUser.userId);
                    if (prefs) {
                        setPreferences(prefs);
                        setTempPreferences({
                            darkMode: prefs.darkMode ?? false,
                            twoFactorMethod: prefs.twoFactorMethod ?? '',
                            formOpacity: prefs.formOpacity ?? 1.0,
                            formBlur: prefs.formBlur ?? 1.0,
                            backgroundBlur: prefs.backgroundBlur ?? 0.0,
                            backgroundImage: prefs.backgroundImage,
                            baseColor: prefs.baseColor || "#3498db"
                        });
                        setTempTwoFactorMethod(prefs.twoFactorMethod);
                    } else {
                        // If no preferences exist, initialize with empty values
                        const emptyPrefs = {
                            darkMode: false,
                            twoFactorMethod: null,
                            formOpacity: 1.0,
                            formBlur: 1.0,
                            backgroundBlur: 0.0,
                            backgroundImage: null,
                            baseColor: "#3498db"
                        };
                        setPreferences(emptyPrefs);
                        setTempPreferences(emptyPrefs);
                        setTempTwoFactorMethod(null);
                    }
                } catch (error) {
                    console.error('Failed to load preferences:', error);
                    // Initialize with empty values on error
                    const emptyPrefs = {
                        darkMode: false,
                        twoFactorMethod: null,
                        formOpacity: 1.0,
                        formBlur: 1.0,
                        backgroundBlur: 0.0,
                        backgroundImage: null,
                        baseColor: "#3498db"
                    };
                    setPreferences(emptyPrefs);
                    setTempPreferences(emptyPrefs);
                    setTempTwoFactorMethod(null);
                }
            }
        } catch (error) {
            console.error('Failed to load user data:', error);
            setError('Failed to load user data');
        } finally {
            setLoading(false);
        }
    };

    // Helper function to calculate portion value
    const calculatePortionValue = (appointmentsList, xPortionValue) => {
        const baseSum = appointmentsList.reduce((acc, curr) => {
            const employmentTypeMultiplier = curr.employmentTypeMultiplier || 1.0;
            return acc + (Number(curr?.roleImportance || 0) * employmentTypeMultiplier);
        }, 0);
        return baseSum * xPortionValue;
    };

    const fetchUserAppointments = async () => {
        try {
            setLoadingAppointments(true);
            const [userAppointments, allAppointments, xPortionData] = await Promise.all([
                appointmentService.getAppointmentsByUserId(authUser.userId),
                appointmentService.getAllAppointments(),
                XPortionService.getLatest()
            ]);
            
            setAppointments(userAppointments || []);
            
            // Calculate total organization portion value
            const xPortionValue = xPortionData?.value || 1.0;
            const totalPortion = calculatePortionValue(allAppointments, xPortionValue);
            setTotalOrgPortionValue(totalPortion);
        } catch (error) {
            console.error('Failed to load user appointments:', error);
            // Don't set error state to avoid disrupting the whole profile view
        } finally {
            setLoadingAppointments(false);
        }
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault(); // Prevent form submission and page refresh
        try {
            setSaving(true);
            const formData = new FormData();
            
            // Append profile fields - only send if they have a value
            if (userInfo.firstName) formData.append('firstName', userInfo.firstName);
            if (userInfo.lastName) formData.append('lastName', userInfo.lastName);
            if (userInfo.gender) formData.append('gender', userInfo.gender);
            if (userInfo.birthDate) formData.append('birthDate', userInfo.birthDate.toISOString());
            if (userInfo.phoneNumber) formData.append('phoneNumber', userInfo.phoneNumber);
            if (userInfo.mobileNumber) formData.append('mobileNumber', userInfo.mobileNumber);
            
            // Required fields - always send
            formData.append('address', userInfo.address || '');
            formData.append('postCode', userInfo.postCode || '');
            formData.append('imageUrl', userData?.imageUrl || '');

            // Append profile image if provided
            if (selectedImage) {
                formData.append('profileImage', selectedImage);
            }

            await userService.updateProfile(authUser.userId, formData);
            toast.success('Profile updated successfully');
            fetchUserData();
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        
        // Check if new password and confirm password are filled
        if (!passwordData.newPassword || !passwordData.confirmPassword) {
            toast.error('Please fill in both new password and confirm password fields');
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        try {
            setSaving(true);
            await authService.changePassword(
                authUser.userId,
                passwordData.oldPassword,
                passwordData.newPassword,
                passwordData.confirmPassword
            );
            toast.success('Password changed successfully');
            setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePreferenceChange = (darkMode) => {
        setTempPreferences(prev => ({ ...prev, darkMode }));
    };

    const handleOpacityChange = (opacity) => {
        // Convert from frontend (0-100) to database (0-1)
        const decimalValue = parseInt(opacity) / 100;
        setTempPreferences(prev => ({ ...prev, formOpacity: decimalValue }));
    };

    const handleBlurChange = (blur) => {
        // Convert from frontend (0-100) to database (0-1)
        const decimalValue = parseInt(blur) / 100;
        setTempPreferences(prev => ({ ...prev, formBlur: decimalValue }));
    };

    const handleBackgroundBlurChange = (blur) => {
        // Convert from frontend (0-100) to database (0-1)
        const decimalValue = parseInt(blur) / 100;
        setTempPreferences(prev => ({ ...prev, backgroundBlur: decimalValue }));
    };

    const handleBaseColorChange = (color) => {
        setTempPreferences(prev => ({ ...prev, baseColor: color }));
    };

    const handleBackgroundImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        
        setSelectedImage(file);
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        
        // Clean up the old preview URL when component unmounts
        return () => URL.revokeObjectURL(previewUrl);
    };

    const handleRemoveBackground = async () => {
        try {
            setSaving(true);
            
            // If there's an existing background image, delete it first
            if (tempPreferences?.backgroundImage) {
                try {
                    const oldPath = tempPreferences.backgroundImage.replace(`${API_BASE_URL}`, '');
                    await FileService.deleteFile(oldPath);
                } catch (deleteError) {
                    console.error("Error deleting background image:", deleteError);
                }
            }

            // Update preferences without background image
            const formData = new FormData();
            formData.append('darkMode', tempPreferences?.darkMode ? 'true' : 'false');
            formData.append('twoFactorMethod', tempPreferences?.twoFactorMethod || 'null');
            formData.append('formOpacity', tempPreferences?.formOpacity?.toString() || '1.0');
            formData.append('formBlur', tempPreferences?.formBlur?.toString() || '1.0');
            formData.append('backgroundBlur', tempPreferences?.backgroundBlur?.toString() || '0.0');
            formData.append('baseColor', tempPreferences?.baseColor || "#3498db");
            formData.append('backgroundImagePath', ''); // Explicitly set empty string to clear background image

            await userPreferenceService.saveUserPreference(authUser.userId, formData);

            // Update local state
            setTempPreferences(prev => ({ ...prev, backgroundImage: null }));
            setSelectedImage(null);
            setImagePreview(null);
            toast.success('Background image removed successfully!');
        } catch (error) {
            console.error('Failed to remove background image:', error);
            toast.error('Failed to remove background image');
        } finally {
            setSaving(false);
        }
    };

    const handleResetPreferences = async () => {
        try {
            setSaving(true);
            // Get current preferences to preserve 2FA settings
            const currentPrefs = await userPreferenceService.getUserPreference(authUser.userId);
            
            // Create new preferences with default values but keep 2FA
            const newPreferences = {
                darkMode: false,
                twoFactorMethod: currentPrefs?.twoFactorMethod || null,
                formOpacity: 1.0,
                formBlur: 1.0,
                backgroundBlur: 0.0,
                backgroundImage: null,
                baseColor: "#3498db"
            };

            // Update temp preferences state
            setTempPreferences(newPreferences);
            
            // Create form data for saving
            const formData = new FormData();
            formData.append('darkMode', 'false');
            formData.append('twoFactorMethod', newPreferences.twoFactorMethod || 'null');
            formData.append('formOpacity', '1.0');
            formData.append('formBlur', '1.0');
            formData.append('backgroundBlur', '0.0');
            formData.append('backgroundImagePath', '');
            formData.append('baseColor', "#3498db");

            // Save to server
            await userPreferenceService.saveUserPreference(authUser.userId, formData);

            // Update local state with new preferences
            const updatedPrefs = await userPreferenceService.getUserPreference(authUser.userId);
            setPreferences(updatedPrefs);

            // Apply changes immediately if in modal
            if (onClose) {
                onClose(updatedPrefs);
            }

            toast.success('Preferences reset to default values');
        } catch (error) {
            console.error('Failed to reset preferences:', error);
            toast.error('Failed to reset preferences');
        } finally {
            setSaving(false);
        }
    };

    const handleApplyPreferences = async () => {
        try {
            setSaving(true);
            
            const formData = new FormData();
            
            // Always append the preference values
            formData.append('darkMode', tempPreferences?.darkMode ? 'true' : 'false');
            formData.append('twoFactorMethod', tempPreferences?.twoFactorMethod || 'null');
            formData.append('formOpacity', tempPreferences?.formOpacity?.toString() || '1.0');
            formData.append('formBlur', tempPreferences?.formBlur?.toString() || '1.0');
            formData.append('backgroundBlur', tempPreferences?.backgroundBlur?.toString() || '0.0');
            
            // Handle background image
            if (selectedImage) {
                // If we have a new image to upload, append it as a file
                formData.append('backgroundImage', selectedImage);
            } else if (tempPreferences?.backgroundImage) {
                // If we have an existing image path, append it as a string
                formData.append('backgroundImagePath', tempPreferences.backgroundImage);
            }

            formData.append('baseColor', tempPreferences?.baseColor || "#3498db");

            await userPreferenceService.saveUserPreference(authUser.userId, formData);

            const updatedPrefs = await userPreferenceService.getUserPreference(authUser.userId);
            setPreferences(updatedPrefs);
            setTempPreferences({
                darkMode: Boolean(updatedPrefs.darkMode),
                twoFactorMethod: updatedPrefs.twoFactorMethod,
                formOpacity: Number(updatedPrefs.formOpacity) || 1.0,
                formBlur: Number(updatedPrefs.formBlur) || 1.0,
                backgroundBlur: Number(updatedPrefs.backgroundBlur) || 0.0,
                backgroundImage: updatedPrefs.backgroundImage,
                baseColor: updatedPrefs.baseColor || "#3498db"
            });

            // Apply the changes immediately
            if (onClose) {
                onClose(updatedPrefs); // Pass the updated preferences back to parent
            }
            
            setSelectedImage(null);
            toast.success('Preferences updated successfully');
        } catch (error) {
            toast.error('Failed to update preferences');
        } finally {
            setSaving(false);
        }
    };

    const handle2FAChange = async () => {
        try {
            setSaving(true);
            const preferenceData = {
                darkMode: Boolean(preferences?.darkMode),
                twoFactorMethod: tempTwoFactorMethod === 'null' ? null : tempTwoFactorMethod,
                formOpacity: Number(preferences?.formOpacity) || 1.0,
                formBlur: Number(preferences?.formBlur) || 1.0,
                backgroundImage: preferences?.backgroundImage,
                baseColor: preferences?.baseColor || "#3498db"
            };
            await userPreferenceService.saveUserPreference(authUser.userId, preferenceData);
            setPreferences(prev => ({ ...prev, twoFactorMethod: tempTwoFactorMethod === 'null' ? null : tempTwoFactorMethod }));
            toast.success('Two-factor authentication settings updated');
        } catch (error) {
            toast.error('Failed to update two-factor authentication settings');
        } finally {
            setSaving(false);
        }
    };

    const toggleRowExpand = (index) => {
        setExpandedRowIndex(expandedRowIndex === index ? null : index);
    };

    if (!authUser?.userId || loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                    <p className="mt-3 fs-5">Loading your profile...</p>
                </div>
            </Container>
        );
    }

    return (
        <div className="profile-container">
            {/* Left Panel */}
            <div className="profile-left-panel p-3">
                <div className="text-center mb-3">
                    <ProfileImage 
                        imageUrl={userData?.imageUrl}
                        width={100}
                        height={100}
                        className="rounded-circle border shadow-sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => document.getElementById('profile-image-upload').click()}
                    />
                    <h6 className="mt-2 mb-0 text-truncate" style={{ maxWidth: '100%' }}>{userData?.firstName} {userData?.lastName}</h6>
                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '100%' }}>{userData?.email}</small>
                    <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        id="profile-image-upload"
                    />
                    <Button
                        variant="primary"
                        size="sm"
                        className="mt-2"
                        onClick={() => document.getElementById('profile-image-upload').click()}
                    >
                        <i className="fas fa-camera me-2" style={{ fontSize: '0.8rem' }}></i>
                        Change Picture
                    </Button>
                </div>
                
                <Nav className="flex-column nav-pills">
                    <Nav.Link 
                        active={activeTab === 'info'}
                        onClick={() => setActiveTab('info')}
                        className="mb-1 py-2"
                        style={{ 
                            color: authUser?.darkMode ? '#fff' : '#333',
                            backgroundColor: activeTab === 'info' 
                                ? `rgba(${preferences?.baseColor ? parseInt(preferences.baseColor.slice(1, 3), 16) + ',' + parseInt(preferences.baseColor.slice(3, 5), 16) + ',' + parseInt(preferences.baseColor.slice(5, 7), 16) : '52,152,219'}, 0.2)` 
                                : 'transparent'
                        }}
                    >
                        <FaUser className="me-2" /> 
                        <small>User Information</small>
                    </Nav.Link>
                    <Nav.Link 
                        active={activeTab === 'organization'}
                        onClick={() => setActiveTab('organization')}
                        className="mb-1 py-2"
                        style={{ 
                            color: authUser?.darkMode ? '#fff' : '#333',
                            backgroundColor: activeTab === 'organization' 
                                ? `rgba(${preferences?.baseColor ? parseInt(preferences.baseColor.slice(1, 3), 16) + ',' + parseInt(preferences.baseColor.slice(3, 5), 16) + ',' + parseInt(preferences.baseColor.slice(5, 7), 16) : '52,152,219'}, 0.2)` 
                                : 'transparent'
                        }}
                    >
                        <FaBuilding className="me-2" /> 
                        <small>Organizational Info</small>
                    </Nav.Link>
                    <Nav.Link 
                        active={activeTab === 'security'}
                        onClick={() => setActiveTab('security')}
                        className="mb-1 py-2"
                        style={{ 
                            color: authUser?.darkMode ? '#fff' : '#333',
                            backgroundColor: activeTab === 'security' 
                                ? `rgba(${preferences?.baseColor ? parseInt(preferences.baseColor.slice(1, 3), 16) + ',' + parseInt(preferences.baseColor.slice(3, 5), 16) + ',' + parseInt(preferences.baseColor.slice(5, 7), 16) : '52,152,219'}, 0.2)` 
                                : 'transparent'
                        }}
                    >
                        <FaLock className="me-2" /> 
                        <small>Security</small>
                    </Nav.Link>
                    <Nav.Link 
                        active={activeTab === 'preferences'}
                        onClick={() => setActiveTab('preferences')}
                        className="mb-1 py-2"
                        style={{ 
                            color: authUser?.darkMode ? '#fff' : '#333',
                            backgroundColor: activeTab === 'preferences' 
                                ? `rgba(${preferences?.baseColor ? parseInt(preferences.baseColor.slice(1, 3), 16) + ',' + parseInt(preferences.baseColor.slice(3, 5), 16) + ',' + parseInt(preferences.baseColor.slice(5, 7), 16) : '52,152,219'}, 0.2)` 
                                : 'transparent'
                        }}
                    >
                        <FaPalette className="me-2" /> 
                        <small>Preferences</small>
                    </Nav.Link>
                </Nav>
            </div>

            {/* Right Panel */}
            <div className="profile-right-panel">
                <div className="card-header py-2 px-3">
                    <h6 className="mb-0">
                        {activeTab === 'info' && <><FaUser className="me-2 text-primary" /> User Information</>}
                        {activeTab === 'organization' && <><FaBuilding className="me-2 text-primary" /> Organizational Information</>}
                        {activeTab === 'security' && <><FaLock className="me-2 text-primary" /> Security Settings</>}
                        {activeTab === 'preferences' && <><FaPalette className="me-2 text-primary" /> User Preferences</>}
                    </h6>
                </div>
                <div className="card-body p-3">
                    {activeTab === 'info' && (
                        <Form onSubmit={handleSave} className="profile-form">
                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">First Name</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.firstName}
                                            onChange={(e) => setUserInfo({...userInfo, firstName: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Last Name</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.lastName}
                                            onChange={(e) => setUserInfo({...userInfo, lastName: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Email</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="email"
                                            value={userInfo.email}
                                            onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Phone Number</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.phoneNumber}
                                            onChange={(e) => setUserInfo({...userInfo, phoneNumber: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Mobile Number</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.mobileNumber}
                                            onChange={(e) => setUserInfo({...userInfo, mobileNumber: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Address</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.address}
                                            onChange={(e) => setUserInfo({...userInfo, address: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small mb-1">Post Code</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="text"
                                            value={userInfo.postCode}
                                            onChange={(e) => setUserInfo({...userInfo, postCode: e.target.value})}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="text-end mt-3">
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    size="sm"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                                            Saving...
                                        </>
                                    ) : 'Save Changes'}
                                </Button>
                            </div>
                        </Form>
                    )}

                    {activeTab === 'organization' && (
                        <div>
                            <h5 className="border-bottom">
                                <FaBriefcase className="me-2" /> Positions & Appointments
                            </h5>
                            
                            {loadingAppointments ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2">Loading organizational information...</p>
                                </div>
                            ) : appointments.length === 0 ? (
                                <Alert variant="info">
                                    No organizational appointments found for this user.
                                </Alert>
                            ) : (
                                <>
                                    <div className="mb-3 text-muted small">
                                        <i>Click on a row to view more details about the position</i>
                                    </div>
                                    <div className="responsive-table-container">
                                        <Table responsive hover className="responsive-table" style={{
                                            fontSize: '0.75rem'
                                        }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ fontSize: '0.75rem' }}>Organization Unit</th>
                                                    <th style={{ fontSize: '0.75rem' }}>Position</th>
                                                    <th style={{ fontSize: '0.75rem' }}>Role</th>
                                                    <th style={{ fontSize: '0.75rem' }}>Employment Type</th>
                                                    <th style={{ fontSize: '0.75rem' }}>Start Date</th>
                                                    <th style={{ fontSize: '0.75rem' }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {appointments.map((appointment, index) => (
                                                    <tr 
                                                        key={index}
                                                        onClick={() => toggleRowExpand(index)}
                                                        className={expandedRowIndex === index ? "bg-light" : ""}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>
                                                            <div className="fw-medium" style={{ fontSize: '0.75rem' }}>{appointment.organizationalUnitName}</div>
                                                        </td>
                                                        <td style={{ fontSize: '0.75rem' }}>{appointment.positionTitle}</td>
                                                        <td style={{ fontSize: '0.75rem' }}>{appointment.roleName || '-'}</td>
                                                        <td>
                                                            <Badge bg="info" pill className="text-white" style={{ fontSize: '0.625rem' }}>
                                                                {appointment.employmentType}
                                                            </Badge>
                                                            {appointment.xPortion && appointment.xPortion < 1 && (
                                                                <span className="ms-2 text-muted" style={{ fontSize: '0.625rem' }}>
                                                                    ({Math.round(appointment.xPortion * 100)}%)
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td style={{ fontSize: '0.75rem' }}>{new Date(appointment.startDate).toLocaleDateString()}</td>
                                                        <td>
                                                            {!appointment.endDate ? (
                                                                <Badge bg="success" style={{ fontSize: '0.625rem' }}>Active</Badge>
                                                            ) : new Date(appointment.endDate) > new Date() ? (
                                                                <Badge bg="warning" text="dark" style={{ fontSize: '0.625rem' }}>Ending {new Date(appointment.endDate).toLocaleDateString()}</Badge>
                                                            ) : (
                                                                <Badge bg="secondary" style={{ fontSize: '0.625rem' }}>Inactive</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                    
                                    {expandedRowIndex !== null && (
                                        <Card className="profile-expanded-card">
                                            <Card.Header className="profile-expanded-header">
                                                <h5 className="mb-0">Position Details</h5>
                                                <Button 
                                                    variant="light" 
                                                    size="sm" 
                                                    onClick={() => setExpandedRowIndex(null)}
                                                    className="profile-close-button"
                                                >
                                                    <FaTimes />
                                                </Button>
                                            </Card.Header>
                                            <Card.Body className="profile-expanded-body">
                                                <div className="position-relative">
                                                    <h6 className="mb-1 pb-1">
                                                        {appointments[expandedRowIndex].positionTitle} at {appointments[expandedRowIndex].organizationalUnitName}
                                                    </h6>
                                                    
                                                    <Row>
                                                                        <Col md={12} className="mb-3">
                                                                            <h6 className="mb-3 pb-2 border-bottom" style={{ fontSize: '1.1rem', fontWeight: '500' }}>Position Details</h6>
                                                            <Row className="g-2">
                                                                                <Col>
                                                                                    <div className="d-flex align-items-center gap-4">
                                                                                        <div>
                                                                        <div className="text-muted small">Role</div>
                                                                        <div className="fw-medium">{appointments[expandedRowIndex].roleName || 'Not specified'}</div>
                                                                    </div>
                                                                                        <div>
                                                                        <div className="text-muted small">Employment Type</div>
                                                                        <div className="fw-medium">
                                                                            <Badge 
                                                                                bg={appointments[expandedRowIndex].employmentType === 'Full Time' ? 'primary' : appointments[expandedRowIndex].employmentType === 'Part Time' ? 'info' : 'secondary'} 
                                                                                pill
                                                                            >
                                                                                {appointments[expandedRowIndex].employmentType}
                                                                            </Badge>
                                                                            {appointments[expandedRowIndex].employmentTypeMultiplier && (
                                                                                <span className="ms-2 text-muted">
                                                                                    (×{appointments[expandedRowIndex].employmentTypeMultiplier})
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                                        <div>
                                                                        <div className="text-muted small">Start Date</div>
                                                                        <div className="fw-medium">{new Date(appointments[expandedRowIndex].startDate).toLocaleDateString()}</div>
                                                                    </div>
                                                                                        <div>
                                                                        <div className="text-muted small">End Date</div>
                                                                        <div className="fw-medium">
                                                                            {appointments[expandedRowIndex].endDate ? new Date(appointments[expandedRowIndex].endDate).toLocaleDateString() : 'Ongoing'}
                                                                        </div>
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                            </Row>
                                                        </Col>

                                                                        <Col md={12} className="mb-3">
                                                                            <h6 className="mb-3 pb-2 border-bottom" style={{ fontSize: '1.1rem', fontWeight: '500' }}>Role Importance</h6>
                                                                            <div className="text-muted small mb-2">
                                                                                Formula: Experience Level * Educational Level * Organizational Level * Position Multiplier * Impact Factor
                                                                            </div>
                                                                            <div className="d-flex align-items-center gap-4">
                                                                                <div>
                                                                            <div className="text-muted small">Experience Level</div>
                                                                            <div className="fw-medium">
                                                                                {appointments[expandedRowIndex].experienceLevelName}
                                                                                    <span className="ms-2 text-muted">
                                                                                        (×{appointments[expandedRowIndex].experienceLevelMultiplier})
                                                                                    </span>
                                                                            </div>
                                                                        </div>
                                                                                <div>
                                                                            <div className="text-muted small">Education Level</div>
                                                                            <div className="fw-medium">
                                                                                {appointments[expandedRowIndex].educationLevelName}
                                                                                    <span className="ms-2 text-muted">
                                                                                        (×{appointments[expandedRowIndex].educationlevelMultiplier})
                                                                                    </span>
                                                                            </div>
                                                                        </div>
                                                                                <div>
                                                                            <div className="text-muted small">Organizational Level</div>
                                                                            <div className="fw-medium">
                                                                                {appointments[expandedRowIndex].organizationalLevelName}
                                                                                    <span className="ms-2 text-muted">
                                                                                        (×{appointments[expandedRowIndex].organizationalLevelMultiplier})
                                                                                    </span>
                                                                            </div>
                                                                        </div>
                                                                                <div>
                                                                        <div className="text-muted small">Position Multiplier</div>
                                                                                    <div className="fw-medium">×{appointments[expandedRowIndex].positionMultiplier}</div>
                                                                    </div>
                                                                                <div>
                                                                        <div className="text-muted small">Impact Factor</div>
                                                                                    <div className="fw-medium">×{appointments[expandedRowIndex].impactFactor}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-muted small">Role Importance</div>
                                                                                    <div className="fw-bold">{appointments[expandedRowIndex].roleImportance}</div>
                                                                                </div>
                                                                    </div>
                                                                </Col>

                                                                        <Col md={12} className="mb-3">
                                                                            <h6 className="mb-3 pb-2 border-bottom" style={{ fontSize: '1.1rem', fontWeight: '500' }}>Added Value</h6>
                                                                            <div className="text-muted small mb-2">
                                                                                Formula: Time * Role Importance
                                                                            </div>
                                                                            <div className="d-flex align-items-center gap-4">
                                                                                <div>
                                                                                    <div className="text-muted small">Time</div>
                                                                                    <div className="fw-medium">{appointments[expandedRowIndex].employmentTypeMultiplier}</div>
                                                                                </div>
                                                                                <div>
                                                                        <div className="text-muted small">Role Importance</div>
                                                                                    <div className="fw-medium">{appointments[expandedRowIndex].roleImportance}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-muted small">Added Value</div>
                                                                                                                                                                        <div className="fw-bold">
                                                                                        {(appointments[expandedRowIndex].employmentTypeMultiplier * appointments[expandedRowIndex].roleImportance).toFixed(2)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>

                                                                        <Col md={12} className="mb-3">
                                                                            <h6 className="mb-3 pb-2 border-bottom" style={{ fontSize: '1.1rem', fontWeight: '500' }}>Portion Value</h6>
                                                                            <div className="text-muted small mb-2">
                                                                                Formula: X Portion * Added Value
                                                                            </div>
                                                                            <div className="d-flex align-items-center gap-4">
                                                                                <div>
                                                                                    <div className="text-muted small">X Portion</div>
                                                                                    <div className="fw-medium">{appointments[expandedRowIndex].xPortion}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-muted small">My Portion Value</div>
                                                                                    <div className="fw-medium">
                                                                                        {(appointments[expandedRowIndex].xPortion * appointments[expandedRowIndex].employmentTypeMultiplier * appointments[expandedRowIndex].roleImportance).toFixed(2)}
                                                                                    </div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-muted small">Total Employees Portions Values</div>
                                                                                    <div className="fw-medium">{totalOrgPortionValue}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <div className="text-muted small">My Portion Percentage</div>
                                                                                    <div className="fw-bold">
                                                                                        {((appointments[expandedRowIndex].xPortion * appointments[expandedRowIndex].employmentTypeMultiplier * appointments[expandedRowIndex].roleImportance / totalOrgPortionValue) * 100).toFixed(2)}%
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    )}
                                    
                                    <div className="appointments-summary">
                                        <div className="d-flex align-items-center">
                                            <div className="me-4">
                                                <div className="text-muted small">Total Positions</div>
                                                <div className="fw-bold">{appointments.length}</div>
                                            </div>
                                            <div className="me-4">
                                                <div className="text-muted small">Active Positions</div>
                                                <div className="fw-bold">
                                                    {appointments.filter(a => !a.endDate || new Date(a.endDate) > new Date()).length}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-muted small">Primary Role</div>
                                                <div className="fw-bold">
                                                    {appointments.length > 0 ? 
                                                        appointments.sort((a, b) => 
                                                            (b.roleImportance || 0) - (a.roleImportance || 0)
                                                        )[0].positionTitle : 'None'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div>
                            <div className="mb-3">
                                <h5 className="mb-2 border-bottom pb-2" style={{ fontSize: '1.1rem' }}>Change Password</h5>
                                <Form onSubmit={handlePasswordChange}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Current Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={passwordData.oldPassword}
                                            onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})}
                                            className="shadow-sm"
                                            size="sm"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={passwordData.newPassword}
                                            onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                                            className="shadow-sm"
                                            size="sm"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Confirm New Password</Form.Label>
                                        <Form.Control
                                            type="password"
                                            value={passwordData.confirmPassword}
                                            onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                                            className="shadow-sm"
                                            size="sm"
                                        />
                                    </Form.Group>

                                    <div className="text-end">
                                        <Button 
                                            type="submit" 
                                            variant="primary" 
                                            size="sm"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                                                    Changing...
                                                </>
                                            ) : 'Change Password'}
                                        </Button>
                                    </div>
                                </Form>
                            </div>

                            <div className="mt-3">
                                <h5 className="mb-2 border-bottom pb-2" style={{ fontSize: '1.1rem' }}>
                                    <FaShieldAlt className="me-2" style={{ fontSize: '0.9rem' }} /> Two-Factor Authentication
                                    {preferences?.twoFactorMethod && (
                                        <Badge bg="success" className="small ms-2">Enabled</Badge>
                                    )}
                                </h5>
                                
                                <Form.Group className="mb-2 two-factor-options">
                                    <div className="d-flex flex-column gap-1">
                                        <Form.Check
                                            type="radio"
                                            id="2fa-disabled"
                                            label={<span className="small">Disabled</span>}
                                            name="2fa"
                                            checked={tempTwoFactorMethod === null}
                                            onChange={() => setTempTwoFactorMethod(null)}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="2fa-email"
                                            label={
                                                <div className="d-flex align-items-center">
                                                    <FaEnvelope className="me-2 text-primary" style={{ fontSize: '0.8rem' }} />
                                                    <span className="small">Email Authentication</span>
                                                </div>
                                            }
                                            name="2fa"
                                            checked={tempTwoFactorMethod === 'Email'}
                                            onChange={() => setTempTwoFactorMethod('Email')}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="2fa-sms"
                                            label={
                                                <div className="d-flex align-items-center">
                                                    <FaMobile className="me-2 text-primary" style={{ fontSize: '0.8rem' }} />
                                                    <span className="small">SMS Authentication</span>
                                                </div>
                                            }
                                            name="2fa"
                                            checked={tempTwoFactorMethod === 'SMS'}
                                            onChange={() => setTempTwoFactorMethod('SMS')}
                                        />
                                    </div>
                                </Form.Group>
                                
                                <div className="text-end">
                                    <Button 
                                        variant="primary" 
                                        onClick={handle2FAChange}
                                        disabled={saving || tempTwoFactorMethod === preferences?.twoFactorMethod}
                                        size="sm"
                                    >
                                        {saving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" className="me-1" />
                                                Updating...
                                            </>
                                        ) : 'Apply 2FA Settings'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'preferences' && (
                        <div>
                            <h5 className="mb-3 border-bottom pb-2" style={{ fontSize: '1.1rem' }}>Display Preferences</h5>
                            
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="dark-mode-switch"
                                    label={
                                        <div className="d-flex align-items-center">
                                            <span className="small me-2">Dark Mode</span>
                                            {tempPreferences?.darkMode && <Badge bg="dark" className="small">Enabled</Badge>}
                                        </div>
                                    }
                                    checked={tempPreferences?.darkMode || false}
                                    onChange={(e) => handlePreferenceChange(e.target.checked)}
                                />
                                <p className="text-muted mt-1 small" style={{ fontSize: '0.8rem' }}>
                                    Enable dark mode for a more comfortable viewing experience in low-light environments.
                                </p>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small">Theme Base Color</Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Control
                                        type="color"
                                        value={tempPreferences?.baseColor || "#3498db"}
                                        onChange={(e) => handleBaseColorChange(e.target.value)}
                                        title="Choose your base color"
                                        className="color-picker"
                                    />
                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                        This color will be used to tint the interface theme
                                    </small>
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="d-flex justify-content-between align-items-center small">
                                    <span>Form Opacity</span>
                                    <Badge bg="info" className="small ms-2">{Math.round((tempPreferences?.formOpacity || 1) * 100)}%</Badge>
                                </Form.Label>
                                <Form.Range
                                    min={0}
                                    max={100}
                                    value={Math.round((tempPreferences?.formOpacity || 1) * 100)}
                                    onChange={(e) => handleOpacityChange(e.target.value)}
                                    className="mt-1"
                                />
                                <p className="text-muted mt-1 small" style={{ fontSize: '0.8rem' }}>
                                    Adjust the opacity of forms and cards throughout the application.
                                </p>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="d-flex justify-content-between align-items-center small">
                                    <span>Form Blur Effect</span>
                                    <Badge bg="info" className="small ms-2">{Math.round((tempPreferences?.formBlur || 1) * 100)}%</Badge>
                                </Form.Label>
                                <Form.Range
                                    min={0}
                                    max={100}
                                    value={Math.round((tempPreferences?.formBlur || 1) * 100)}
                                    onChange={(e) => handleBlurChange(e.target.value)}
                                    className="mt-1"
                                />
                                <p className="text-muted mt-1 small" style={{ fontSize: '0.8rem' }}>
                                    Adjust the blur effect of the glass morphism throughout the application.
                                </p>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="d-flex justify-content-between align-items-center small">
                                    <span>Background Blur Effect</span>
                                    <Badge bg="info" className="small ms-2">{Math.round((tempPreferences?.backgroundBlur || 0) * 100)}%</Badge>
                                </Form.Label>
                                <Form.Range
                                    min={0}
                                    max={100}
                                    value={Math.round((tempPreferences?.backgroundBlur || 0) * 100)}
                                    onChange={(e) => handleBackgroundBlurChange(e.target.value)}
                                    className="mt-1"
                                />
                                <p className="text-muted mt-1 small" style={{ fontSize: '0.8rem' }}>
                                    Adjust the blur effect of the background image.
                                </p>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="small">Background Image</Form.Label>
                                <div className="d-flex align-items-start gap-2 mt-1">
                                    {(tempPreferences?.backgroundImage || imagePreview) && (
                                        <div className="position-relative" style={{ width: '110px', height: '70px' }}>
                                            <img
                                                src={imagePreview || (tempPreferences.backgroundImage.startsWith('http') 
                                                    ? tempPreferences.backgroundImage 
                                                    : `${API_BASE_URL}${tempPreferences.backgroundImage}`)}
                                                alt="Background"
                                                className="rounded"
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <Button 
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute top-0 end-0 m-1"
                                                onClick={handleRemoveBackground}
                                                style={{ padding: '0.2rem 0.4rem' }}
                                            >
                                                <FaTimes style={{ fontSize: '0.8rem' }} />
                                            </Button>
                                        </div>
                                    )}
                                    <div className="flex-grow-1">
                                        <Form.Control
                                            type="file"
                                            accept="image/*"
                                            onChange={handleBackgroundImageChange}
                                            className="mb-1"
                                            size="sm"
                                        />
                                        <p className="text-muted small mb-0" style={{ fontSize: '0.8rem' }}>
                                            Upload a background image to personalize your experience. Recommended size: 1920x1080px.
                                        </p>
                                    </div>
                                </div>
                            </Form.Group>

                            <div className="text-end mt-3">
                                <Button 
                                    variant="primary" 
                                    onClick={handleApplyPreferences}
                                    disabled={saving}
                                    size="sm"
                                >
                                    {saving ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            Applying...
                                        </>
                                    ) : 'Apply Changes'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile; 